import React, {useState} from 'react';
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import {SMALL_BLUE_BUTTON_SX} from '../SettingsPills';
import useNotification from '../../../../utility/hooks/useNotification';
import {
    useAddSchoolStockBoxMutation,
    useDisposeSchoolStockBoxMutation,
    useGetSchoolStockQuery,
    useSetMedicineOtcConsentMutation,
    useSetSchoolStockReorderLevelMutation,
    useUndoDisposeSchoolStockBoxMutation
} from '../../../../api/services/medicinesService';
import {useGetMedicalConsentOptionsQuery} from '../../../../api/services/lookupsService';
import {
    countInUnit,
    EXPIRY_DAY_LABEL,
    expiryDays,
    expiryMonthText,
    lastDayOfMonth,
    MEDICINE_BOX_MAX,
    MONTH_NAMES
} from '../../../../utility/medicines';

const thisYear = new Date().getFullYear();
const YEARS = Array.from({length: 11}, (_, i) => thisYear - 1 + i);
const emptyBox = {amount: '', day: '', month: '', year: ''};

// A box past its expiry date reads in red, as on a child's Medicine at School.
export const isExpired = (expiryDate, today = new Date()) =>
    Boolean(expiryDate) && expiryDate < today.toISOString().slice(0, 10);

// The amount left in the school's stock: every box that has not been disposed of and has not expired, as no
// dose can be given from an expired box. The server counts the Medicine at School list column the same way.
export const stockLeft = (boxes, today = new Date()) => (boxes ?? [])
    .filter(box => !box.disposedAt && !isExpired(box.expiryDate, today))
    .reduce((sum, box) => sum + box.dosesRemaining, 0);

export const amountProblem = amount => {
    const n = Number(amount);
    return !Number.isInteger(n) || n < 1 || n > MEDICINE_BOX_MAX
        ? `Enter the amount in this box, as a whole number up to ${MEDICINE_BOX_MAX.toLocaleString('en-GB')}`
        : '';
};

// AD-2715: the school's own stock of one medicine on the Medicine at School list. A School Admin adds a box,
// disposes of a box, and chooses the item on the school's Non-Prescription (OTC) Medication list that covers it.
const SchoolStockDialog = ({medicine, units, onClose, onChanged}) => {
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {data: boxes = [], refetch} = useGetSchoolStockQuery(medicine.id, {refetchOnMountOrArgChange: true});
    const {data: otc} = useGetMedicalConsentOptionsQuery();
    const [addBox] = useAddSchoolStockBoxMutation();
    const [disposeBox] = useDisposeSchoolStockBoxMutation();
    const [undoDisposeBox] = useUndoDisposeSchoolStockBoxMutation();
    const [setOtc] = useSetMedicineOtcConsentMutation();
    const [setReorderLevel] = useSetSchoolStockReorderLevelMutation();
    // AD-2804: the school is warned when its own stock is at or below this amount
    const [reorder, setReorder] = useState(medicine.schoolStockReorderLevel ?? '');
    const [boxForm, setBoxForm] = useState(emptyBox);
    const [otcId, setOtcId] = useState(medicine.medicalConsentLookupId ?? '');
    const otcItems = (otc?.options ?? []).filter(item => item.isActive !== false);
    const count = amount => countInUnit(Number(amount ?? 0), medicine.dosageUnit, units);

    const run = async (action, message) => {
        try {
            await action().unwrap();
            await refetch();
            onChanged();
            showSuccessNotification(message);
            return true;
        } catch (error) {
            showErrorNotification(error?.data?.validationErrors?.[0]?.errorMessage ?? 'That could not be saved');
            return false;
        }
    };

    const handleAdd = async () => {
        const saved = await run(() => addBox({
            lookupId: medicine.id,
            numberOfItems: 1,
            dosesPerItem: Number(boxForm.amount),
            expiryDate: lastDayOfMonth(Number(boxForm.year), Number(boxForm.month)),
            expiryDay: boxForm.day ? Number(boxForm.day) : null
        }), 'Box added to the school stock');
        if (saved) setBoxForm(emptyBox);
    };

    const handleOtc = async value => {
        setOtcId(value);
        await run(() => setOtc({lookupId: medicine.id, medicalConsentLookupId: value === '' ? null : Number(value)}), 'OTC consent saved');
    };

    const reorderProblem = reorder !== '' && !(Number.isInteger(Number(reorder)) && Number(reorder) >= 0)
        ? 'Enter the reorder level as a whole number'
        : '';

    const handleReorder = async () => {
        if (reorderProblem || String(reorder) === String(medicine.schoolStockReorderLevel ?? '')) return;
        await run(() => setReorderLevel({lookupId: medicine.id, reorderLevel: reorder === '' ? null : Number(reorder)}), 'Reorder level saved');
    };

    const canAdd = !amountProblem(boxForm.amount) && boxForm.month && boxForm.year;

    return (
        <AdmicityDialog
            open
            maxWidth="md"
            title={`School stock: ${medicine.description}`}
            handleClose={onClose}
            actions={[{label: 'Close', onClick: onClose}]}
        >
            <Stack gap={2} sx={{mt: 1}} data-testid="school-stock-dialog">
                <TextField
                    select
                    fullWidth
                    label="Non-Prescription (OTC) Medication consent"
                    data-testid="school-stock-otc"
                    value={otcId}
                    onChange={event => handleOtc(event.target.value)}
                >
                    <MenuItem value="">None</MenuItem>
                    {otcItems.map(item => <MenuItem key={item.id} value={item.id}>{item.description}</MenuItem>)}
                </TextField>

                <TextField
                    label={`Reorder level (${count(2).replace(/^2 /, '')})`}
                    type="number"
                    fullWidth
                    data-testid="school-stock-reorder"
                    inputProps={{min: 0, step: 1}}
                    error={Boolean(reorderProblem)}
                    helperText={reorderProblem}
                    value={reorder}
                    onChange={event => setReorder(event.target.value)}
                    onBlur={handleReorder}
                />

                <Typography variant="subtitle2" data-testid="school-stock-left">
                    In stock: {count(stockLeft(boxes))}
                </Typography>

                {boxes.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{'& th': {backgroundColor: '#e3f5f1', fontWeight: 600, py: 0.5}}}>
                                <TableCell>Expiry</TableCell>
                                <TableCell>Doses in box</TableCell>
                                <TableCell>Remaining</TableCell>
                                <TableCell align="right"/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {boxes.map(box => {
                                const expired = !box.disposedAt && isExpired(box.expiryDate);
                                return (
                                    <TableRow
                                        key={box.id}
                                        data-testid={`school-box-${box.id}`}
                                        sx={{opacity: box.disposedAt ? 0.55 : 1, '& td': expired ? {color: 'error.main'} : {}}}
                                    >
                                        <TableCell>{expiryMonthText(box.expiryDate) || 'No expiry date'}</TableCell>
                                        <TableCell>{box.numberOfItems * box.dosesPerItem}</TableCell>
                                        <TableCell data-testid={`school-box-${box.id}-remaining`}>
                                            {box.disposedAt ? 'Disposed of' : box.dosesRemaining}
                                        </TableCell>
                                        <TableCell align="right">
                                            {!box.disposedAt && (
                                                <Tooltip title="Mark as disposed of">
                                                    <IconButton
                                                        size="small"
                                                        data-testid={`school-box-${box.id}-dispose`}
                                                        onClick={() => run(() => disposeBox(box.id), 'Box marked as disposed of')}
                                                    >
                                                        <DeleteSweepOutlinedIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {box.disposedAt && (
                                                <Tooltip title="Put back in use">
                                                    <IconButton
                                                        size="small"
                                                        data-testid={`school-box-${box.id}-undo-dispose`}
                                                        onClick={() => run(() => undoDisposeBox(box.id), 'Box put back in use')}
                                                    >
                                                        <UndoIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}

                <Box sx={{border: '1px solid #e3e8ef', borderRadius: 1, p: 2}}>
                    <Typography variant="subtitle2" sx={{mb: 1.5}}>Add a box</Typography>
                    <Stack gap={2}>
                        <TextField
                            label={`Doses in box (${count(2).replace(/^2 /, '')})`}
                            type="number"
                            fullWidth
                            data-testid="school-box-amount"
                            inputProps={{min: 1, max: MEDICINE_BOX_MAX, step: 1}}
                            error={boxForm.amount !== '' && Boolean(amountProblem(boxForm.amount))}
                            helperText={boxForm.amount !== '' ? amountProblem(boxForm.amount) : ''}
                            value={boxForm.amount}
                            onChange={event => setBoxForm({...boxForm, amount: event.target.value})}
                        />
                        <TextField
                            select
                            fullWidth
                            label={EXPIRY_DAY_LABEL}
                            data-testid="school-box-expiry-day"
                            value={boxForm.day}
                            onChange={event => setBoxForm({...boxForm, day: event.target.value})}
                        >
                            <MenuItem value="">&nbsp;</MenuItem>
                            {expiryDays(boxForm.year, boxForm.month).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </TextField>
                        <Stack direction="row" gap={2}>
                            <TextField
                                select
                                fullWidth
                                label="Expiry month"
                                data-testid="school-box-expiry-month"
                                value={boxForm.month}
                                onChange={event => setBoxForm({...boxForm, month: event.target.value, day: Number(boxForm.day) > expiryDays(boxForm.year || 2000, event.target.value).length ? '' : boxForm.day})}
                            >
                                {MONTH_NAMES.map((name, i) => (
                                    <MenuItem key={name} value={String(i + 1).padStart(2, '0')}>{name}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                fullWidth
                                label="Expiry year"
                                data-testid="school-box-expiry-year"
                                value={boxForm.year}
                                onChange={event => setBoxForm({...boxForm, year: event.target.value})}
                            >
                                {YEARS.map(y => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                            </TextField>
                        </Stack>
                        <Box>
                            <Button
                                variant="contained"
                                sx={SMALL_BLUE_BUTTON_SX}
                                data-testid="school-box-add"
                                disabled={!canAdd}
                                onClick={handleAdd}
                            >
                                Add box
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </AdmicityDialog>
    );
};

SchoolStockDialog.propTypes = {
    medicine: PropTypes.shape({
        id: PropTypes.number.isRequired,
        description: PropTypes.string,
        dosageUnit: PropTypes.string,
        medicalConsentLookupId: PropTypes.number,
        schoolStockReorderLevel: PropTypes.number
    }).isRequired,
    units: PropTypes.array,
    onClose: PropTypes.func.isRequired,
    onChanged: PropTypes.func.isRequired
};

export default SchoolStockDialog;
